/** CubicleSubInvitePanel — the "invite my assistant / tailor / audition
 *  candidate" module that lives INSIDE every cubicle.
 *
 *  Rules:
 *    • Anyone who's a member of THIS cubicle (dept crew, sub-member,
 *      or project owner) can invite others by picking them from the
 *      live search (name / email / phone / membership no) OR by typing
 *      an off-platform phone.
 *    • Invitees get a pending tile on their /me/desk with
 *      ``scope="cubicle_only"`` and can only access this one cubicle.
 *    • The panel also lists current sub-members (with status pill).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  UserPlus, Users, ShieldCheck, Clock, X, Send, Search, Check,
  Award, Wallet, Lock, Eye, EyeOff, MessageCircle, Paperclip,
  Megaphone, Image as ImageIcon, FileText, Music, File as FileIcon,
} from "lucide-react";

export default function CubicleSubInvitePanel({ projectId, cubicleKey,
                                                cubicleLabel, accent,
                                                canHire = false }) {
  const [subs, setSubs]     = useState([]);
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);   // {id, name, email, phone, ...}
  const [phone,  setPhone]  = useState("");     // off-platform fallback
  const [name,   setName]   = useState("");     // off-platform fallback
  const [companyName, setCompanyName] = useState("");   // Iter-93 Phase A · legal + insurance
  const [contactType, setContactType] = useState("individual"); // 'individual' | 'company'
  const [roleLabel, setRoleLabel] = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(false);   // 403 → hide panel
  const [hireFor, setHireFor] = useState(null);   // sub-member row we're hiring
  const [dmFor, setDmFor]   = useState(null);     // sub-member row we're DMing 1-on-1
  const [broadcastOpen, setBroadcastOpen] = useState(false);  // Iter-99 · group broadcast modal
  const searchTimer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/cubicles/${cubicleKey}/sub-members`);
      setSubs(data.items || []);
      setError(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError(true);
      }
    }
  };

  useEffect(() => { void load(); }, [projectId, cubicleKey]);

  // Live search — debounced 220ms, min 2 chars, hits /api/crew/lookup
  // with the cubicle key so the result set is department-scoped
  // (a Costumes search can never surface a stunt performer).
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (picked || query.trim().length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/crew/lookup",
          { params: { q: query, cubicle: cubicleKey } });
        setResults(data.results || []);
      } catch (e) {
        // silent — network hiccups shouldn't spam the toast tray while typing
      }
    }, 220);
    return () => clearTimeout(searchTimer.current);
  }, [query, picked, cubicleKey]);

  const resetForm = () => {
    setQuery(""); setResults([]); setPicked(null);
    setPhone(""); setName(""); setRoleLabel("");
    setCompanyName(""); setContactType("individual");
  };

  const submit = async () => {
    if (!roleLabel.trim()) {
      toast.error("Role label is required (e.g. 'Master Tailor')");
      return;
    }
    if (!picked && !phone.trim() && !companyName.trim()) {
      toast.error("Pick a person, dial a phone number, or add a company / firm name");
      return;
    }
    setBusy(true);
    try {
      const body = picked
        ? { user_id: picked.id, role_label: roleLabel.trim() }
        : { phone:        phone.trim(),
            name:         name.trim() || undefined,
            company_name: companyName.trim() || undefined,
            contact_type: contactType || undefined,
            role_label:   roleLabel.trim() };
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/sub-invites`,
        body,
      );
      const target = picked?.name || companyName.trim() || name.trim() || phone.trim();
      toast.success(picked
        ? `Invited ${picked.name} into ${cubicleLabel}`
        : `Off-platform invite recorded for ${target} — they'll get a nudge when we integrate SMS.`);
      resetForm();
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (error) return null;   // caller isn't a cubicle member — hide silently

  return (
    <div className="cs-card rounded-2xl p-5 mt-6"
         data-testid={`sub-invite-panel-${cubicleKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
               style={{background:`${accent}22`}}>
            <Users size={14} style={{color: accent}}/>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
              Sub-invites · {cubicleLabel}
            </div>
            <div className="text-sm text-white/70">
              Bring your team into this cubicle only
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canHire && subs.some((s) => s.user_id && ["accepted","hired"].includes(s.invite_status || "invited")) && (
            <Button onClick={() => setBroadcastOpen(true)}
                    className="h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px] border border-white/15 bg-transparent text-white/85 hover:text-white hover:bg-white/[0.06]"
                    data-testid={`sub-invite-broadcast-open-${cubicleKey}`}
                    title="Message every accepted crew member of this cubicle at once">
              <Megaphone size={12}/> Message all
            </Button>
          )}
          <Button onClick={() => { setOpen(!open); if (open) resetForm(); }}
                  className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
                  data-testid={`sub-invite-toggle-${cubicleKey}`}>
            <UserPlus size={12}/> {open ? "Close" : "Invite"}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-3"
             data-testid={`sub-invite-form-${cubicleKey}`}>
          {/* Live search — shows platform users. */}
          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
              Find someone
            </label>
            {picked ? (
              <div className="mt-1 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3"
                   data-testid={`sub-invite-picked-${cubicleKey}`}>
                <div className="w-8 h-8 rounded-full bg-white/8 flex items-center justify-center overflow-hidden">
                  {picked.avatar_url
                    ? <img src={picked.avatar_url} alt="" className="w-full h-full object-cover"/>
                    : <Users size={13} className="text-white/50"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{picked.name}</div>
                  <div className="text-[10px] text-white/55 truncate">
                    {picked.email || picked.phone || picked.role_label || "—"}
                  </div>
                </div>
                <button onClick={() => setPicked(null)}
                        className="text-white/55 text-[10px] uppercase tracking-[0.2em] hover:text-white"
                        data-testid={`sub-invite-clear-${cubicleKey}`}>
                  Change
                </button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
                <Input value={query}
                       onChange={(e) => setQuery(e.target.value)}
                       placeholder="Name, email, phone or membership no…"
                       className="bg-black/40 border-white/15 pl-9"
                       data-testid={`sub-invite-search-${cubicleKey}`}/>
                {results.length > 0 && (
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-black/60"
                       data-testid={`sub-invite-results-${cubicleKey}`}>
                    {results.map((r) => (
                      <button key={r.id}
                              onClick={() => { setPicked(r); setResults([]); }}
                              className="w-full text-left p-2.5 hover:bg-white/[0.06] flex items-center gap-3 border-b border-white/5 last:border-none"
                              data-testid={`sub-invite-result-${r.id}`}>
                        <div className="w-8 h-8 rounded-full bg-white/8 flex items-center justify-center overflow-hidden shrink-0">
                          {r.avatar_url
                            ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover"/>
                            : <Users size={12} className="text-white/50"/>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{r.name || "Unnamed"}</div>
                          <div className="text-[10px] text-white/55 truncate">
                            {r.email || r.phone || r.role_label || "—"}
                            {r.membership_number && (
                              <span className="font-mono text-white/40"> · {r.membership_number}</span>
                            )}
                          </div>
                        </div>
                        {r.is_member && <Check size={12} className="text-emerald-400 shrink-0"/>}
                      </button>
                    ))}
                  </div>
                )}
                {query.trim().length >= 2 && results.length === 0 && (
                  <div className="mt-1 text-[10px] text-white/45 tracking-[0.18em] uppercase">
                    No match — you can still invite off-platform via phone below.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Off-platform fallback — always visible so producers can add
              phone-only crew, insurance brokers, or law firms without a
              search miss. Insurance + Legal cubicles surface an extra
              "company / firm" field because those invitees are often
              organisations rather than individuals. */}
          {!picked && (
            <div className="space-y-3">
              {(cubicleKey === "insurance" || cubicleKey === "legal") && (
                <div className="flex items-center gap-2"
                     data-testid={`sub-invite-contact-type-${cubicleKey}`}>
                  <span className="text-[9px] tracking-[0.22em] uppercase text-white/50">
                    Inviting a
                  </span>
                  {["individual", "company"].map((t) => (
                    <button key={t} type="button"
                            onClick={() => setContactType(t)}
                            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] transition ${
                              contactType === t
                                ? "bg-white text-neutral-900"
                                : "border border-white/15 text-white/70 hover:text-white"
                            }`}
                            data-testid={`contact-type-${t}-${cubicleKey}`}>
                      {t === "individual"
                        ? (cubicleKey === "legal" ? "Individual Counsel" : "Individual")
                        : (cubicleKey === "legal" ? "Law Firm" : "Insurance Co.")}
                    </button>
                  ))}
                </div>
              )}
              {contactType === "company"
                && (cubicleKey === "insurance" || cubicleKey === "legal") && (
                <div>
                  <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
                    {cubicleKey === "legal" ? "Law firm name *" : "Insurance company *"}
                  </label>
                  <Input value={companyName}
                         onChange={(e) => setCompanyName(e.target.value)}
                         placeholder={cubicleKey === "legal"
                           ? "e.g. Alpha Legal LLP"
                           : "e.g. Bajaj Allianz"}
                         className="bg-black/40 border-white/15 mt-1"
                         data-testid={`sub-invite-company-${cubicleKey}`}/>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
                    {contactType === "company" ? "Contact person phone" : "Off-platform phone"}
                  </label>
                  <Input value={phone}
                         onChange={(e) => setPhone(e.target.value)}
                         placeholder="+91…"
                         className="bg-black/40 border-white/15 mt-1"
                         data-testid={`sub-invite-phone-${cubicleKey}`}/>
                </div>
                <div>
                  <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
                    {contactType === "company" ? "Contact person name" : "Off-platform name"}
                  </label>
                  <Input value={name}
                         onChange={(e) => setName(e.target.value)}
                         placeholder={contactType === "company" ? "Ravi Sinha" : "Ravi Kumar"}
                         className="bg-black/40 border-white/15 mt-1"
                         data-testid={`sub-invite-name-${cubicleKey}`}/>
                </div>
              </div>
              <div className="text-[10px] text-white/40 leading-relaxed">
                💡 Phone + WhatsApp / SMS delivery is pending — for now
                the invite is recorded here and the crew member is
                pinged the moment they sign up with the same number.
              </div>
            </div>
          )}

          <div>
            <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">Role label *</label>
            <Input value={roleLabel}
                   onChange={(e) => setRoleLabel(e.target.value)}
                   placeholder="Master Tailor"
                   className="bg-black/40 border-white/15 mt-1"
                   data-testid={`sub-invite-role-${cubicleKey}`}/>
          </div>

          <div className="flex justify-end">
            <Button onClick={submit} disabled={busy}
                    className="btn-themed h-10 px-6 rounded-full uppercase tracking-[0.22em] text-[11px]"
                    data-testid={`sub-invite-submit-${cubicleKey}`}>
              <Send size={13}/> {busy ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </div>
      )}

      {/* Existing sub-members */}
      {subs.length > 0 && (
        <div className="mt-5 space-y-2"
             data-testid={`sub-members-list-${cubicleKey}`}>
          {subs.map((s) => {
            const st = s.invite_status || "invited";
            const pill = st === "hired"    ? "bg-fuchsia-500/15 text-fuchsia-300"
                       : st === "accepted" ? "bg-emerald-500/15 text-emerald-300"
                       : st === "declined" ? "bg-red-500/15 text-red-300"
                       : "bg-amber-500/15 text-amber-300";
            const Icon = st === "hired"    ? Award
                       : st === "accepted" ? ShieldCheck
                       : st === "declined" ? X : Clock;
            return (
              <div key={s.id}
                   className="flex items-center gap-3 border border-white/10 rounded-lg p-3"
                   data-testid={`sub-member-row-${s.id}`}>
                <div className="w-8 h-8 rounded-full bg-white/8 flex items-center justify-center overflow-hidden">
                  {s.avatar_url
                    ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover"/>
                    : <Users size={13} className="text-white/50"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.name}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">
                    {s.role_label} · invited by {s.invited_by_name}
                  </div>
                  {st === "hired" && (
                    <HireAmountLine invite={s} accent={accent} />
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase inline-flex items-center gap-1 ${pill}`}>
                  <Icon size={10}/> {st}
                </span>
                {/* Iter-98 — 1-on-1 DM inside the cubicle. Only shown
                    for platform members (off-platform invitees don't
                    have a user_id to route to yet). */}
                {s.user_id && (
                  <Button size="sm"
                          variant="ghost"
                          onClick={() => setDmFor(s)}
                          className="rounded-full h-8 px-3 uppercase tracking-[0.2em] text-[10px] text-white/80 hover:text-white border border-white/15"
                          data-testid={`sub-member-chat-${s.id}`}
                          title={`Direct message ${s.name} inside this cubicle`}>
                    <MessageCircle size={11}/> Chat
                  </Button>
                )}
                {canHire && st === "accepted" && (
                  <Button size="sm"
                          onClick={() => setHireFor(s)}
                          className="rounded-full h-8 px-4 uppercase tracking-[0.2em] text-[10px]"
                          style={{ background: accent, color: "#0a0812" }}
                          data-testid={`sub-member-hire-${s.id}`}>
                    <Award size={11}/> Hire
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hireFor && (
        <HireDialog
          member={hireFor}
          accent={accent}
          onClose={() => setHireFor(null)}
          onHired={async () => { setHireFor(null); await load(); }}
        />
      )}

      {dmFor && (
        <SubInviteDMDrawer
          member={dmFor}
          cubicleLabel={cubicleLabel}
          accent={accent}
          onClose={() => setDmFor(null)}
        />
      )}

      {broadcastOpen && (
        <CubicleBroadcastModal
          projectId={projectId}
          cubicleKey={cubicleKey}
          cubicleLabel={cubicleLabel}
          accent={accent}
          recipients={subs.filter((s) => s.user_id
            && ["accepted","hired"].includes(s.invite_status || "invited"))}
          onClose={() => setBroadcastOpen(false)}
        />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  SubInviteDMDrawer — 1-on-1 DM with a sub-invitee, opened INSIDE
 *  the cubicle so the producer / HOD doesn't have to leave the room
 *  to message someone they just added.
 *
 *  Uses the existing `/messaging/threads` idempotent create-or-get
 *  DM endpoint (kind="dm", peer_id=<member.user_id>) — the returned
 *  thread is the same object the main Inbox surfaces, so a message
 *  sent here shows up in that user's inbox too. This makes each
 *  cubicle a self-contained ecosystem without forking messaging.
 * ═══════════════════════════════════════════════════════════════════ */
function SubInviteDMDrawer({ member, cubicleLabel, accent, onClose }) {
  const [thread, setThread]   = useState(null);
  const [messages, setMsgs]   = useState([]);
  const [draft, setDraft]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed]   = useState(null);
  const [pendingAtts, setPendingAtts] = useState([]);   // uploaded, not-yet-sent
  const [uploadingCount, setUploadingCount] = useState(0);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  // Idempotent create-or-get the DM thread on mount. This never
  // duplicates — the server pairs users by sorted [id,id].
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/messaging/threads", {
          kind:    "dm",
          peer_id: member.user_id,
          title:   `${cubicleLabel} · ${member.name}`,
        });
        if (!alive) return;
        setThread(data.thread);
        const msgs = await api.get(
          `/messaging/threads/${data.thread.id}/messages`);
        if (!alive) return;
        setMsgs(msgs.data.items || []);
      } catch (e) {
        if (!alive) return;
        setFailed(formatApiErrorDetail(e?.response?.data?.detail)
                  || "Couldn't open the direct-message thread.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [member.user_id, member.name, cubicleLabel]);

  // Auto-scroll to newest message
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    if (!thread) return;
    const hasBody = draft.trim().length > 0;
    const hasAtts = pendingAtts.length > 0;
    if (!hasBody && !hasAtts) return;
    setBusy(true);
    try {
      await api.post(`/messaging/threads/${thread.id}/messages`,
                     { body: draft.trim(), attachments: pendingAtts });
      setDraft("");
      setPendingAtts([]);
      const { data } = await api.get(
        `/messaging/threads/${thread.id}/messages`);
      setMsgs(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";     // allow re-picking the same file
    if (!files.length || !thread) return;
    // 20 MB cap mirrors backend
    for (const f of files) {
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`${f.name} is over 20 MB — pick a smaller file`);
        continue;
      }
      setUploadingCount((n) => n + 1);
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post(
          `/messaging/threads/${thread.id}/attachments`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setPendingAtts((prev) => [...prev, data.attachment]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail)
                    || `Upload failed for ${f.name}`);
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }
  };

  const removePending = (idx) => {
    setPendingAtts((prev) => prev.filter((_, i) => i !== idx));
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid={`sub-invite-dm-${member.id}`}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/8 flex items-center justify-center overflow-hidden">
            {member.avatar_url
              ? <img src={member.avatar_url} alt="" className="w-full h-full object-cover"/>
              : <Users size={16} className="text-white/50"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: accent }}>
              Direct · {cubicleLabel}
            </div>
            <div className="font-display text-lg uppercase tracking-tight truncate">
              {member.name}
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 truncate">
              {member.role_label}
            </div>
          </div>
          <button onClick={onClose}
                  className="text-white/50 hover:text-white p-1"
                  data-testid={`sub-invite-dm-close-${member.id}`}>
            <X size={16}/>
          </button>
        </div>

        <div ref={listRef}
             className="flex-1 overflow-y-auto p-5 space-y-3 bg-black/25"
             data-testid={`sub-invite-dm-thread-${member.id}`}>
          {loading && (
            <div className="text-xs text-white/40 text-center py-6">
              Opening thread…
            </div>
          )}
          {failed && (
            <div className="text-xs text-red-300 text-center py-6"
                 data-testid={`sub-invite-dm-error-${member.id}`}>
              {failed}
            </div>
          )}
          {!loading && !failed && messages.length === 0 && (
            <div className="text-xs text-white/40 text-center py-8">
              Say hi to {member.name} — this direct message is private
              between the two of you and lives inside the {cubicleLabel} cubicle.
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className="flex flex-col"
                 data-testid={`sub-invite-dm-message-${m.id}`}>
              <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">
                {m.sender_name || m.sender_id}
              </div>
              {m.body && (
                <div className="text-sm text-white/85 mt-0.5">{m.body}</div>
              )}
              {(m.attachments || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {m.attachments.map((a, i) => (
                    <MessageAttachment key={i} attachment={a}
                                       threadId={thread?.id}
                                       accent={accent}/>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {pendingAtts.length > 0 && (
          <div className="px-4 pt-3 flex flex-wrap gap-2 border-t border-white/10"
               data-testid={`sub-invite-dm-pending-${member.id}`}>
            {pendingAtts.map((a, i) => (
              <div key={i}
                   className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-black/30 px-2 py-1 text-[11px] text-white/85 max-w-[220px]">
                {a.kind === "image" ? <ImageIcon size={12}/>
                  : a.kind === "pdf" ? <FileText size={12}/>
                  : a.kind === "audio" ? <Music size={12}/>
                  : <FileIcon size={12}/>}
                <span className="truncate">{a.filename}</span>
                <button onClick={() => removePending(i)}
                        className="text-white/50 hover:text-red-300"
                        data-testid={`sub-invite-dm-pending-remove-${member.id}-${i}`}>
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}
        {uploadingCount > 0 && (
          <div className="px-4 pt-2 text-[10px] text-white/50">
            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
          </div>
        )}

        <div className="p-4 border-t border-white/10 flex gap-2 items-center">
          <input type="file" ref={fileInputRef} className="hidden"
                 accept="image/*,application/pdf,audio/*,.doc,.docx,.xlsx,.txt"
                 multiple onChange={onPickFile}
                 data-testid={`sub-invite-dm-file-input-${member.id}`}/>
          <button type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!thread || busy}
                  className="h-10 w-10 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/30 flex items-center justify-center shrink-0 disabled:opacity-40"
                  title="Attach an image, PDF, voice note or file (max 20 MB each)"
                  data-testid={`sub-invite-dm-attach-${member.id}`}>
            <Paperclip size={14}/>
          </button>
          <Input value={draft}
                 onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
                 placeholder={`Message ${member.name.split(" ")[0]}…`}
                 disabled={!thread || busy}
                 className="flex-1 bg-black/40 border-white/15"
                 data-testid={`sub-invite-dm-input-${member.id}`}/>
          <Button onClick={send}
                  disabled={!thread || busy
                            || (!draft.trim() && pendingAtts.length === 0)}
                  className="rounded-full px-5 uppercase tracking-[0.22em] text-[11px]"
                  style={{ background: accent, color: "#0a0812" }}
                  data-testid={`sub-invite-dm-send-${member.id}`}>
            <Send size={12}/>
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  MessageAttachment — RBAC-safe renderer for image / PDF / audio /
 *  file bubbles inside a DM message.
 *
 *  Serve endpoint requires the Authorization header, so images can't
 *  be dropped in a plain <img src>. We fetch bytes via the axios
 *  instance (which injects auth) and hydrate an object URL.
 * ═══════════════════════════════════════════════════════════════════ */
function MessageAttachment({ attachment, threadId, accent }) {
  const [objUrl, setObjUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!threadId || attachment.kind !== "image") return;
    let alive = true;
    let cleanupUrl = null;
    (async () => {
      try {
        const r = await api.get(
          `/messaging/threads/${threadId}/attachments/serve`,
          { params: { path: attachment.path }, responseType: "blob" },
        );
        if (!alive) return;
        cleanupUrl = URL.createObjectURL(r.data);
        setObjUrl(cleanupUrl);
      } catch (e) {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
      if (cleanupUrl) URL.revokeObjectURL(cleanupUrl);
    };
  }, [threadId, attachment.path, attachment.kind]);

  const download = async () => {
    try {
      const r = await api.get(
        `/messaging/threads/${threadId}/attachments/serve`,
        { params: { path: attachment.path }, responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Couldn't download that file");
    }
  };

  if (attachment.kind === "image") {
    if (failed) {
      return (
        <div className="text-[11px] text-red-300 px-2 py-1 rounded bg-red-500/10">
          Image failed to load
        </div>
      );
    }
    return (
      <button type="button" onClick={download}
              className="rounded-lg overflow-hidden max-w-[220px] border border-white/10 hover:border-white/30 block"
              data-testid={`dm-attachment-image-${attachment.path}`}
              title={`Click to download ${attachment.filename}`}>
        {objUrl ? (
          <img src={objUrl} alt={attachment.filename || ""}
               className="w-full h-auto block max-h-56 object-cover"/>
        ) : (
          <div className="w-56 h-32 bg-black/50 flex items-center justify-center">
            <ImageIcon size={18} className="text-white/40"/>
          </div>
        )}
      </button>
    );
  }

  const Icon = attachment.kind === "pdf"   ? FileText
             : attachment.kind === "audio" ? Music
             : FileIcon;
  return (
    <button type="button" onClick={download}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-black/30 hover:bg-black/50 px-2.5 py-1.5 max-w-[240px]"
            data-testid={`dm-attachment-file-${attachment.path}`}
            style={{ color: accent }}>
      <Icon size={13}/>
      <span className="text-[11px] text-white/85 truncate">
        {attachment.filename || attachment.kind}
      </span>
    </button>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  CubicleBroadcastModal (Iter-99) — one-tap "message all crew".
 *
 *  Producer / master keys compose a single message (+ optional
 *  attachments) and it fans out to every accepted sub-invitee's DM
 *  via `POST /projects/:pid/cubicles/:key/broadcast`. The recipient
 *  sees it in their existing DM thread with the producer — no fork,
 *  no new inbox surface.
 * ═══════════════════════════════════════════════════════════════════ */
function CubicleBroadcastModal({ projectId, cubicleKey, cubicleLabel,
                                  accent, recipients, onClose }) {
  const [body, setBody]     = useState("");
  const [atts, setAtts]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    // For broadcast we upload each file to ONE thread (the sender's
    // own first-recipient DM would be arbitrary), so instead we hit
    // /projects/…/attachments — but our current model stores files
    // per-thread. Simpler path: upload to the first recipient's DM
    // thread; the same emergent path is referenced in every fanned
    // message. To keep the code lean, we skip attachments on
    // broadcast v1 and let the producer send them via the 1-on-1 DM.
    toast.info("File drops in broadcast are coming next release — send them via the 1-on-1 chat for now");
  };

  const submit = async () => {
    const b = body.trim();
    if (!b && atts.length === 0) {
      toast.error("Type a message before broadcasting");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/broadcast`,
        { body: b, attachments: atts },
      );
      toast.success(`Broadcast to ${data.dispatched} crew member${data.dispatched === 1 ? "" : "s"}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail)
                  || "Broadcast failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid={`cubicle-broadcast-modal-${cubicleKey}`}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl w-full max-w-lg">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center"
               style={{ background: `${accent}22`, color: accent }}>
            <Megaphone size={16}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: accent }}>
              Broadcast · {cubicleLabel}
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              Message all crew
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 mt-0.5">
              {recipients.length} recipient{recipients.length === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={onClose}
                  className="text-white/50 hover:text-white p-1"
                  data-testid={`cubicle-broadcast-close-${cubicleKey}`}>
            <X size={16}/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {recipients.length === 0 ? (
            <div className="text-xs text-white/50 text-center py-4">
              No accepted crew yet — invite someone first, then broadcast lands as a group DM.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5"
                   data-testid={`cubicle-broadcast-recipients-${cubicleKey}`}>
                {recipients.slice(0, 12).map((r) => (
                  <span key={r.id}
                        className="text-[10px] tracking-[0.16em] uppercase text-white/70 border border-white/12 rounded-full px-2 py-0.5">
                    {r.name}
                  </span>
                ))}
                {recipients.length > 12 && (
                  <span className="text-[10px] tracking-[0.16em] uppercase text-white/50">
                    +{recipients.length - 12} more
                  </span>
                )}
              </div>
              <div>
                <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
                  Message
                </label>
                <Textarea value={body}
                          onChange={(e) => setBody(e.target.value)}
                          placeholder={`e.g. Basecamp at 06:00, ${cubicleLabel} crew — full kit.`}
                          className="bg-black/40 border-white/15 mt-1 min-h-[120px]"
                          data-testid={`cubicle-broadcast-body-${cubicleKey}`}/>
              </div>
              <input type="file" ref={fileRef} className="hidden"
                     onChange={onPickFile}/>
            </>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex gap-3 justify-end items-center">
          <Button variant="ghost" onClick={onClose}
                  className="text-white/60"
                  data-testid={`cubicle-broadcast-cancel-${cubicleKey}`}>
            Cancel
          </Button>
          <Button onClick={submit}
                  disabled={busy || recipients.length === 0
                            || (!body.trim() && atts.length === 0)}
                  className="rounded-full h-10 px-6 uppercase tracking-[0.22em] text-[11px]"
                  style={{ background: accent, color: "#0a0812" }}
                  data-testid={`cubicle-broadcast-submit-${cubicleKey}`}>
            <Megaphone size={12}/> {busy ? "Sending…" : `Send to ${recipients.length}`}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  HireAmountLine — inline RBAC-filtered amount display.
 *  Fetches the hire row via GET /cubicle-hires/{invite_id}, which
 *  returns can_see_amount=false for non-authorised viewers. Those
 *  viewers get a locked chip.
 * ═══════════════════════════════════════════════════════════════════ */
function HireAmountLine({ invite, accent }) {
  const [row, setRow] = useState(null);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/cubicle-hires/${invite.id}`)
      .then(({ data }) => { if (alive) setRow(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [invite.id]);
  if (!row) return null;
  if (!row.can_see_amount) {
    return (
      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-white/45"
           data-testid={`hire-amount-locked-${invite.id}`}>
        <Lock size={10}/> Amount visible to accounts
      </div>
    );
  }
  const amount = row.row?.hire_amount;
  const currency = row.row?.hire_currency || "INR";
  if (amount == null) {
    return (
      <div className="mt-1 text-[10px] text-white/50"
           data-testid={`hire-amount-empty-${invite.id}`}>
        Amount not entered yet — accounts can fill it in.
      </div>
    );
  }
  const formatted = new Intl.NumberFormat("en-IN", {
    style: "currency", currency, maximumFractionDigits: 0,
  }).format(amount / 100);
  return (
    <button type="button"
            onClick={() => setReveal((r) => !r)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] tabular-nums"
            style={{ color: accent }}
            data-testid={`hire-amount-line-${invite.id}`}>
      <Wallet size={11}/>
      {reveal ? formatted : "•••• hidden"}
      {reveal ? <EyeOff size={10}/> : <Eye size={10}/>}
    </button>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *  HireDialog — producer confirms the hire, optionally captures the
 *  amount (in paise). Amount can be added later by accounts if left
 *  blank here.
 * ═══════════════════════════════════════════════════════════════════ */
function HireDialog({ member, accent, onClose, onHired }) {
  const [amount, setAmount] = useState("");     // rupees, string
  const [notes, setNotes]   = useState("");
  const [busy, setBusy]     = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const paise = amount.trim() === ""
        ? null
        : Math.round(parseFloat(amount) * 100);
      if (paise !== null && (!Number.isFinite(paise) || paise < 0)) {
        toast.error("Enter a valid amount (₹).");
        setBusy(false); return;
      }
      await api.post(`/cubicle-invites/${member.id}/hire`, {
        amount:   paise,
        currency: "INR",
        notes:    notes.trim() || undefined,
      });
      toast.success(`${member.name} is hired · accounts notified`);
      await onHired();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Hire failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="hire-dialog">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-neutral-950 border border-white/15 rounded-2xl p-6 max-w-md w-full">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{color: accent}}>
          Confirm hire
        </div>
        <div className="font-display text-2xl mt-2 uppercase tracking-tight">
          {member.name}
        </div>
        <div className="mt-1 text-xs text-white/60">{member.role_label}</div>

        <div className="mt-5">
          <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
            Budget (₹ · optional)
          </label>
          <Input value={amount}
                 onChange={(e) => setAmount(e.target.value)}
                 placeholder="e.g. 45000"
                 type="number" min="0" step="1"
                 className="bg-black/40 border-white/15 mt-1"
                 data-testid="hire-amount-input"/>
          <div className="mt-1 text-[10px] text-white/45">
            <Lock size={9} className="inline mr-1"/>
            Only accounts, the producer, and {member.name} will see this amount.
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[9px] tracking-[0.22em] uppercase text-white/50">
            Notes (optional)
          </label>
          <Input value={notes}
                 onChange={(e) => setNotes(e.target.value)}
                 placeholder="Payment split · dates · special terms"
                 className="bg-black/40 border-white/15 mt-1"
                 data-testid="hire-notes-input"/>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose}
                  className="text-white/60"
                  data-testid="hire-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-full h-10 px-6 uppercase tracking-[0.22em] text-[11px]"
                  style={{ background: accent, color: "#0a0812" }}
                  data-testid="hire-confirm-btn">
            <Award size={12}/> {busy ? "Confirming…" : "Confirm hire"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
